// src/services/RssImportService.js

import { db } from '../firebase';
import { 
  collection, 
  addDoc, 
  doc, 
  getDoc,
  setDoc, 
  updateDoc,
  serverTimestamp, 
  getDocs, 
  query, 
  where,
  arrayUnion,
  limit
} from 'firebase/firestore';

/**
 * Extract domain from RSS feed URL
 * @param {string} url - The URL to extract domain from
 * @returns {string|null} - The domain or null if invalid
 */
const extractDomain = (url) => {
  try {
    const parsedUrl = new URL(url);
    return parsedUrl.hostname;
  } catch (error) {
    console.error("Error extracting domain:", error);
    return null;
  }
};

/**
 * Imports a podcast from an RSS feed with ownership verification
 * @param {string} rssUrl - The URL of the RSS feed
 * @param {string} userId - The Firebase UID of the user
 * @param {string} username - The username of the creator
 * @returns {Promise<Object>} - Result of the import operation
 */
export const importPodcastFromRSS = async (rssUrl, userId, username) => {
  try {
    // Validation
    if (!userId || !username) {
      throw new Error('User information is required');
    }
    
    if (!rssUrl) {
      throw new Error('RSS feed URL is required');
    }
    
    // Check if user has verified this feed
    const userDoc = await getDoc(doc(db, 'users', userId));
    const userData = userDoc.data();
    const verifiedFeeds = userData?.verifiedFeeds || [];
    
    if (!verifiedFeeds.includes(rssUrl)) {
      // Not verified yet, start verification process
      const domain = extractDomain(rssUrl);
      
      if (!domain) {
        throw new Error('Invalid RSS feed URL format');
      }
      
      const verification = await initiateVerification(rssUrl, userId, domain);
      
      return {
        success: false,
        requiresVerification: true,
        verification: verification,
        message: "Podcast ownership needs to be verified before import"
      };
    }

    // 1. Fetch and parse RSS feed
    const response = await fetch(`https://api.rss2json.com/v1/api.json?rss_url=${encodeURIComponent(rssUrl)}`);
    const data = await response.json();
    
    if (data.status !== 'ok') {
      throw new Error('Failed to parse RSS feed: ' + (data.message || 'Unknown error'));
    }
    
    // 2. Check if podcast already exists
    const podcastsRef = collection(db, 'podcasts');
    const q = query(podcastsRef, where('feedUrl', '==', rssUrl), limit(1));
    const querySnapshot = await getDocs(q);
    
    if (!querySnapshot.empty) {
      throw new Error('Podcast already exists in our database');
    }
    
    // 3. Create podcast document with standardized creator identification
    const podcastData = {
      title: data.feed.title,
      description: data.feed.description,
      author: data.feed.author,
      image: data.feed.image,
      feedUrl: rssUrl,
      link: data.feed.link,
      creatorId: userId,             // Primary identifier (UID)
      creatorUsername: username,     // Secondary identifier (for display)
      dateImported: serverTimestamp(),
      lastUpdated: serverTimestamp(),
      categoryId: '',                // Can be updated later
      episodeCount: data.items.length,
      isVerified: true,              // Since we verified the feed
      verificationDate: serverTimestamp()
    };
    
    const podcastRef = await addDoc(collection(db, 'podcasts'), podcastData);
    const podcastId = podcastRef.id;
    
    // 4. Create user-podcast relationship document
    await setDoc(doc(db, 'user_podcasts', `${userId}_${podcastId}`), {
      userId: userId,
      podcastId: podcastId,
      role: 'owner',
      dateAdded: serverTimestamp()
    });
    
    // 5. Update user profile to include podcast reference
    await updateDoc(doc(db, 'users', userId), {
      podcasts: arrayUnion(podcastId),
      isCreator: true,
      lastUpdated: serverTimestamp()
    });
    
    // 6. Add episodes as subcollection
    const episodesPromises = data.items.map(async (item, index) => {
      const episodeData = {
        title: item.title,
        description: item.description,
        pubDate: new Date(item.pubDate),
        link: item.link,
        guid: item.guid || `${podcastId}-episode-${index}`, // Fallback if GUID not provided
        audioUrl: item.enclosure?.link || '',
        duration: item.enclosure?.duration || '',
        image: item.thumbnail || data.feed.image,
        episodeIndex: index, // To help with ordering
        creatorId: userId,    // Adding creator ID at episode level for additional security
      };
      
      return setDoc(doc(collection(db, `podcasts/${podcastId}/episodes`)), episodeData);
    });
    
    await Promise.all(episodesPromises);
    
    return {
      success: true,
      podcastId: podcastId,
      message: `Successfully imported ${data.items.length} episodes`,
      podcast: {
        title: data.feed.title,
        image: data.feed.image,
        episodeCount: data.items.length
      }
    };
  } catch (error) {
    console.error('RSS Import error:', error);
    return {
      success: false,
      message: error.message || 'Failed to import podcast',
      error: error
    };
  }
};

/**
 * Verify podcast ownership through domain verification
 * @param {string} rssUrl - The RSS feed URL
 * @param {string} userId - The user's UID
 * @param {string} domain - The domain to verify
 * @returns {Promise<Object>} - Verification information
 */
export const initiateVerification = async (rssUrl, userId, domain) => {
  try {
    if (!domain) {
      domain = extractDomain(rssUrl);
    }
    
    if (!domain) {
      throw new Error("Invalid URL format");
    }
    
    // Generate a unique verification token
    const verificationToken = `faircast-verify-${userId.substring(0, 8)}-${Date.now().toString(36)}`;
    
    // Store the verification request
    await setDoc(doc(db, 'verification_requests', `${userId}_${domain.replace(/\./g, '_')}`), {
      userId,
      domain,
      rssUrl,
      verificationToken,
      status: 'pending',
      createdAt: serverTimestamp(),
      verificationMethod: 'domain',
      attempts: 0
    });
    
    return {
      domain,
      verificationToken,
      instructions: `To verify ownership, create a TXT record with the name '_faircast-verify' and value '${verificationToken}' in your domain's DNS settings.`
    };
  } catch (error) {
    console.error('Verification initiation error:', error);
    throw error;
  }
};

/**
 * Check DNS for domain verification
 * @param {string} domain - The domain to verify
 * @param {string} userId - The user's UID
 * @returns {Promise<Object>} - Verification result
 */
export const checkDomainVerification = async (domain, userId) => {
  try {
    // In a real implementation, you would use a server function or API 
    // to check for TXT records in the domain's DNS
    // This is a simplified placeholder
    
    // Get the verification request
    const verificationDoc = await getDoc(doc(db, 'verification_requests', `${userId}_${domain.replace(/\./g, '_')}`));
    
    if (!verificationDoc.exists()) {
      throw new Error("Verification request not found");
    }
    
    const verificationData = verificationDoc.data();
    
    // Increment attempt counter
    await updateDoc(doc(db, 'verification_requests', `${userId}_${domain.replace(/\./g, '_')}`), {
      attempts: verificationData.attempts + 1,
      lastChecked: serverTimestamp()
    });
    
    // DNS TXT record checking requires a server-side implementation (Cloud Function or backend API)
    // that can perform actual DNS lookups. This cannot be done safely from the client.
    // Use the RSS feed token method (verifyRssFeedOwnership) instead — it is the preferred
    // and fully implemented verification path.
    return {
      verified: false,
      message: "DNS verification is not yet available. Please use the RSS feed token method to verify ownership.",
      useRssMethod: true
    };
  } catch (error) {
    console.error('Domain verification check error:', error);
    return { verified: false, error: error.message };
  }
};

/**
 * Verify ownership through RSS feed modification
 * @param {string} rssUrl - The RSS feed URL
 * @param {string} userId - The user's UID
 * @param {string} verificationToken - Token to look for in feed
 * @returns {Promise<Object>} - Verification result
 */
export const verifyRssFeedOwnership = async (rssUrl, userId, verificationToken) => {
  try {
    // Fetch the RSS feed
    const response = await fetch(`https://api.rss2json.com/v1/api.json?rss_url=${encodeURIComponent(rssUrl)}`);
    const data = await response.json();
    
    if (data.status !== 'ok') {
      throw new Error('Failed to fetch RSS feed');
    }
    
    // Check if verification token exists in the feed description
    if (data.feed.description && data.feed.description.includes(verificationToken)) {
      // Mark as verified
      await updateDoc(doc(db, 'users', userId), {
        verifiedFeeds: arrayUnion(rssUrl),
        lastUpdated: serverTimestamp()
      });
      
      return { verified: true, message: "Feed ownership verified!" };
    }
    
    return { 
      verified: false, 
      message: "Verification token not found in feed description. Please add the token to your podcast description and try again."
    };
  } catch (error) {
    console.error("Feed verification error:", error);
    return { verified: false, error: error.message };
  }
};

/**
 * Generate a verification token for RSS feed verification
 * @param {string} rssUrl - The RSS feed URL
 * @param {string} userId - The user's UID
 * @returns {Promise<Object>} - The verification token
 */
export const generateFeedVerificationToken = async (rssUrl, userId) => {
  try {
    // Generate a unique verification token
    const verificationToken = `faircast-verify-${userId.substring(0, 8)}-${Date.now().toString(36)}`;
    
    // Store the verification request
    await setDoc(doc(db, 'verification_requests', `${userId}_feed_${Date.now()}`), {
      userId,
      rssUrl,
      verificationToken,
      status: 'pending',
      createdAt: serverTimestamp(),
      verificationMethod: 'feed',
      attempts: 0
    });
    
    return {
      verificationToken,
      instructions: `To verify ownership, add the following token to your podcast's description in your hosting platform: ${verificationToken}`
    };
  } catch (error) {
    console.error("Token generation error:", error);
    throw error;
  }
};

/**
 * Submit podcast for manual review by admin
 * @param {string} rssUrl - The RSS feed URL
 * @param {string} userId - The user's UID
 * @param {Object} additionalInfo - Additional information for review
 * @returns {Promise<Object>} - Submission result
 */
export const submitForReview = async (rssUrl, userId, additionalInfo) => {
  try {
    // Create a review request that admins can process
    await addDoc(collection(db, 'podcast_reviews'), {
      rssUrl,
      userId,
      additionalInfo,
      status: 'pending',
      submittedAt: serverTimestamp()
    });
    
    return { 
      success: true, 
      message: "Podcast submitted for review. Our team will verify your ownership within 48 hours." 
    };
  } catch (error) {
    console.error("Review submission error:", error);
    return { success: false, error: error.message };
  }
};

/**
 * Migrates existing podcasts to use the new creator identification system
 * @returns {Promise<Object>} - Result of the migration
 */
export const migrateExistingPodcasts = async () => {
  try {
    const results = {
      total: 0,
      updated: 0,
      skipped: 0,
      errors: []
    };
    
    // Get all podcasts
    const podcastsSnapshot = await getDocs(collection(db, 'podcasts'));
    results.total = podcastsSnapshot.size;
    
    // Process each podcast
    for (const podcastDoc of podcastsSnapshot.docs) {
      try {
        const podcast = podcastDoc.data();
        const podcastId = podcastDoc.id;
        
        // Skip if already has proper creator ID structure
        if (podcast.creatorId && podcast.creatorUsername) {
          results.skipped++;
          continue;
        }
        
        // If we only have username, find the user
        if (!podcast.creatorId && podcast.creatorUsername) {
          const usersQuery = query(
            collection(db, 'users'), 
            where('username', '==', podcast.creatorUsername),
            limit(1)
          );
          
          const userSnapshot = await getDocs(usersQuery);
          
          if (userSnapshot.empty) {
            throw new Error(`No user found with username: ${podcast.creatorUsername}`);
          }
          
          const userId = userSnapshot.docs[0].id;
          
          // Update podcast with proper creatorId
          await updateDoc(doc(db, 'podcasts', podcastId), {
            creatorId: userId,
            lastUpdated: serverTimestamp()
          });
          
          // Create user-podcast relationship
          await setDoc(doc(db, 'user_podcasts', `${userId}_${podcastId}`), {
            userId: userId,
            podcastId: podcastId,
            role: 'owner',
            dateAdded: serverTimestamp()
          });
          
          // Update user profile
          await updateDoc(doc(db, 'users', userId), {
            podcasts: arrayUnion(podcastId),
            isCreator: true,
            lastUpdated: serverTimestamp()
          });
          
          results.updated++;
        } else {
          results.skipped++;
        }
      } catch (error) {
        console.error("Error migrating podcast:", error);
        results.errors.push({
          podcastId: podcastDoc.id,
          error: error.message
        });
      }
    }
    
    return {
      success: true,
      results: results
    };
  } catch (error) {
    console.error('Migration error:', error);
    return {
      success: false,
      message: error.message || 'Failed to migrate podcasts'
    };
  }
};

/**
 * Gets podcasts for a specific user by UID
 * @param {string} userId - The Firebase UID of the user
 * @returns {Promise<Array>} - Array of podcasts
 */
export const getUserPodcasts = async (userId) => {
  try {
    // Query the user_podcasts collection
    const userPodcastsQuery = query(
      collection(db, 'user_podcasts'),
      where('userId', '==', userId)
    );
    
    const userPodcastsSnapshot = await getDocs(userPodcastsQuery);
    
    if (userPodcastsSnapshot.empty) {
      return [];
    }
    
    // Get all podcast IDs
    const podcastIds = userPodcastsSnapshot.docs.map(doc => doc.data().podcastId);
    
    // Fetch each podcast
    const podcastPromises = podcastIds.map(async (podcastId) => {
      const podcastDoc = await getDoc(doc(db, 'podcasts', podcastId));
      if (podcastDoc.exists()) {
        return { id: podcastId, ...podcastDoc.data() };
      }
      return null;
    });
    
    const podcasts = await Promise.all(podcastPromises);
    
    // Filter out any nulls (in case a podcast was deleted)
    return podcasts.filter(podcast => podcast !== null);
  } catch (error) {
    console.error('Error getting user podcasts:', error);
    throw error;
  }
};

/**
 * Get a specific podcast by ID
 * @param {string} podcastId - The podcast ID
 * @returns {Promise<Object|null>} - The podcast data or null if not found
 */
export const getPodcastById = async (podcastId) => {
  try {
    const podcastDoc = await getDoc(doc(db, 'podcasts', podcastId));
    
    if (!podcastDoc.exists()) {
      return null;
    }
    
    return { id: podcastId, ...podcastDoc.data() };
  } catch (error) {
    console.error('Error getting podcast:', error);
    throw error;
  }
};

export default {
  importPodcastFromRSS,
  initiateVerification,
  checkDomainVerification,
  verifyRssFeedOwnership,
  generateFeedVerificationToken,
  submitForReview,
  migrateExistingPodcasts,
  getUserPodcasts,
  getPodcastById
};